import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getPendingArtistRequests, 
  processArtistRequest,
  hasRole
} from '../../services/marketplaceAuthService';
import { ROLES } from '../../firebase.marketplace';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  styled
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const Container = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #030617 0%, #0a1128 100%)',
  padding: theme.spacing(3)
}));

const DashboardContainer = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: 1200,
  margin: '0 auto',
  padding: theme.spacing(3),
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  borderRadius: 10,
  color: 'white'
}));

const DashboardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2)
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(3),
  background: 'rgba(255, 255, 255, 0.05)',
  borderRadius: 10,
  
  '& .MuiTableCell-root': {
    color: 'white',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
  },
  
  '& .MuiTableCell-head': {
    background: 'rgba(0, 0, 0, 0.2)',
    fontWeight: 'bold'
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(0.5)
}));

const ArtistApprovalDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requests, setRequests] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [action, setAction] = useState('');
  const [comments, setComments] = useState('');
  const [processing, setProcessing] = useState(false);
  
  useEffect(() => {
    // Check if user is logged in and is a super admin
    const checkAccess = async () => {
      if (!currentUser) {
        navigate('/marketplace/auth/signin');
        return;
      }
      
      try {
        const superAdminStatus = await hasRole(currentUser.uid, ROLES.SUPER_ADMIN);
        setIsSuperAdmin(superAdminStatus);
        
        if (!superAdminStatus) {
          navigate('/marketplace');
          return;
        }
        
        fetchRequests();
      } catch (error) {
        console.error('Error checking super admin status:', error);
        setError('Failed to verify your permissions. Please try again.');
        setLoading(false);
      }
    };
    
    checkAccess();
  }, [currentUser, navigate]);
  
  const fetchRequests = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const pendingRequests = await getPendingArtistRequests();
      setRequests(pendingRequests);
    } catch (error) {
      console.error('Error fetching artist requests:', error);
      setError('Failed to load artist requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpenDialog = (request, actionType) => {
    setSelectedRequest(request);
    setAction(actionType);
    setComments('');
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRequest(null);
    setAction('');
    setComments('');
  };
  
  const handleProcessRequest = async () => {
    if (!selectedRequest || !action) return;
    
    setProcessing(true);
    
    try {
      await processArtistRequest(
        selectedRequest.id,
        action === 'approve' ? 'approved' : 'rejected',
        currentUser.uid,
        comments
      );
      
      // Remove the processed request from the list
      setRequests(requests.filter(req => req.id !== selectedRequest.id));
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error processing artist request:', error);
      setError(`Failed to ${action} artist request. ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (!isSuperAdmin) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
          <Typography variant="h5" color="error">
            Access Denied. Super Admin privileges required.
          </Typography>
        </Box>
      </Container>
    );
  }
  
  return (
    <Container>
      <DashboardContainer elevation={3}>
        <DashboardHeader>
          <Box>
            <Typography variant="h4" gutterBottom>
              Artist Approval Dashboard
            </Typography>
            <Typography variant="body1" color="rgba(255, 255, 255, 0.7)">
              Review and manage artist requests
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={fetchRequests}
          >
            Refresh Requests
          </Button>
        </DashboardHeader>
        
        {error && (
          <Alert 
            severity="error" 
            style={{ marginBottom: '1.5rem', backgroundColor: 'rgba(211, 47, 47, 0.1)', color: '#f44336' }}
          >
            {error}
          </Alert>
        )}
        
        {requests.length === 0 ? (
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            height="200px"
            bgcolor="rgba(255, 255, 255, 0.05)"
            borderRadius={2}
            p={3}
          >
            <Typography variant="h6" color="rgba(255, 255, 255, 0.7)">
              No pending artist requests at this time
            </Typography>
          </Box>
        ) : (
          <StyledTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Requested On</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar 
                          src={request.user.photoURL} 
                          alt={request.user.displayName}
                          sx={{ marginRight: 2 }}
                        >
                          {request.user.displayName?.charAt(0) || 'U'}
                        </Avatar>
                        {request.user.displayName}
                      </Box>
                    </TableCell>
                    <TableCell>{request.user.email}</TableCell>
                    <TableCell>{formatDate(request.createdAt)}</TableCell>
                    <TableCell>
                      <Chip 
                        label="Pending" 
                        color="warning" 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <ActionButton
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<ApproveIcon />}
                        onClick={() => handleOpenDialog(request, 'approve')}
                      >
                        Approve
                      </ActionButton>
                      <ActionButton
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<RejectIcon />}
                        onClick={() => handleOpenDialog(request, 'reject')}
                      >
                        Reject
                      </ActionButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
        )}
      </DashboardContainer>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        PaperProps={{
          style: {
            backgroundColor: '#1a1a2e',
            color: 'white',
            borderRadius: '10px'
          }
        }}
      >
        <DialogTitle>
          {action === 'approve' ? 'Approve Artist Request' : 'Reject Artist Request'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {action === 'approve'
              ? 'Are you sure you want to approve this artist request? This will grant the user artist privileges on the platform.'
              : 'Are you sure you want to reject this artist request? The user will be notified of the rejection.'}
          </DialogContentText>
          
          {selectedRequest && (
            <Box mt={2} mb={2}>
              <Typography variant="subtitle2" color="rgba(255, 255, 255, 0.7)">
                User: {selectedRequest.user.displayName}
              </Typography>
              <Typography variant="subtitle2" color="rgba(255, 255, 255, 0.7)">
                Email: {selectedRequest.user.email}
              </Typography>
            </Box>
          )}
          
          <TextField
            autoFocus
            margin="dense"
            label="Comments (optional)"
            fullWidth
            multiline
            rows={3}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            variant="outlined"
            placeholder={action === 'approve' 
              ? 'Add any welcome message or instructions for the new artist' 
              : 'Provide a reason for rejection or suggestions for improvement'
            }
            InputLabelProps={{
              style: { color: 'rgba(255, 255, 255, 0.7)' }
            }}
            InputProps={{
              style: { color: 'white' }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4a90e2',
                },
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleProcessRequest} 
            color={action === 'approve' ? 'success' : 'error'}
            variant="contained"
            disabled={processing}
          >
            {processing ? (
              <CircularProgress size={24} />
            ) : action === 'approve' ? (
              'Approve'
            ) : (
              'Reject'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ArtistApprovalDashboard;
